import { z } from 'zod';

import {
  fitsStringified,
  isJsonObject,
  isMemoryLimit,
  isNotUrlLike,
  JSON_FIELD_MAX_LENGTH,
  parseJsonText,
  SMALL_JSON_FIELD_MAX_LENGTH,
  TEXT_FIELD_MAX_LENGTH,
} from './validators';
import {
  type Audience,
  DATA_APP_HIDDEN_FIELDS,
  type EditAppFormValues,
  hasConfigurationFields,
} from './values';

/**
 * Form schemas for the component edit form, one per audience from a shared base.
 *
 * - `superRefine`d for the cross-field code-pattern rule at the bottom.
 */

/**
 * ⚠️ Pinned to http(s): bare `z.url()` accepts `javascript:`; these render as public `href`s.
 */
const httpUrl = z.url({ protocol: /^https?$/ });

/**
 * A URL that may be blank.
 *
 * - ⚠️ Refined, not `z.union`: a failed union hides the branch messages behind "Invalid input".
 * - Ordered, first rule wins: an over-long malformed URL reports only its length.
 */
const optionalUrl = (max: number) =>
  z.string().superRefine((value, ctx) => {
    if (value === '') return;
    if (value.length > max) {
      ctx.addIssue({ code: 'custom', message: `is too long (maximum is ${max} characters)` });
      return;
    }
    if (!httpUrl.safeParse(value).success) {
      ctx.addIssue({ code: 'custom', message: 'is not a valid URL' });
    }
  });

/**
 * A JSON-object text field.
 *
 * - Empty is valid (clears the field); otherwise it must parse to an object.
 * - The ceiling is measured on the stringified parse, so indentation is free.
 * - No argument means no ceiling (only `createConfigurationRowSchema`).
 */
const jsonObjectText = (maximumLength?: number) =>
  z.string().superRefine((text, ctx) => {
    const result = parseJsonText(text);
    if (result.kind === 'empty') return;
    if (result.kind === 'invalid' || !isJsonObject(result.value)) {
      ctx.addIssue({ code: 'custom', message: 'is not valid JSON object' });
      return;
    }
    if (maximumLength !== undefined && !fitsStringified(result.value, maximumLength)) {
      ctx.addIssue({
        code: 'custom',
        message: `is too long (maximum is ${maximumLength} characters)`,
      });
    }
  });

/** A JSON-array text field — `permissions` is the only one. */
const jsonArrayText = () =>
  z.string().superRefine((text, ctx) => {
    const result = parseJsonText(text);
    if (result.kind === 'empty') return;
    if (result.kind === 'invalid' || !Array.isArray(result.value)) {
      ctx.addIssue({ code: 'custom', message: 'is not valid JSON array' });
    }
  });

/** A multi-select whose selections are stringified against a ceiling, as the original's `array` rule is. */
const boundedStringArray = (maximumLength: number) =>
  z
    .array(z.string())
    .refine(
      (value) => fitsStringified(value, maximumLength),
      `is too long (maximum is ${maximumLength} characters)`,
    );

const baseShape = {
  name: z.string().min(1, 'Required').max(128, 'is too long (maximum is 128 characters)'),
  type: z.string().min(1, 'Required').max(20, 'is too long (maximum is 20 characters)'),
  shortDescription: z.string().max(255, 'is too long (maximum is 255 characters)'),
  longDescription: z
    .string()
    .max(TEXT_FIELD_MAX_LENGTH, `is too long (maximum is ${TEXT_FIELD_MAX_LENGTH} characters)`),
  licenseUrl: optionalUrl(255),
  documentationUrl: optionalUrl(255),
  sourceCodeUrl: optionalUrl(255),
  repository: z.object({
    type: z.string(),
    uri: z
      .string()
      .max(255, 'is too long (maximum is 255 characters)')
      .refine(
        isNotUrlLike,
        'cannot be an URL. Please use only [repository/]vendor/component part.',
      ),
    tag: z.string(),
  }),
  defaultBucket: z.boolean(),
  defaultBucketStage: z.string(),
  uiOptions: boundedStringArray(SMALL_JSON_FIELD_MAX_LENGTH),
  imageParameters: jsonObjectText(JSON_FIELD_MAX_LENGTH),
  stackParameters: jsonObjectText(JSON_FIELD_MAX_LENGTH),
  configurationSchema: jsonObjectText(JSON_FIELD_MAX_LENGTH),
  configurationRowSchema: jsonObjectText(JSON_FIELD_MAX_LENGTH),
  emptyConfiguration: jsonObjectText(JSON_FIELD_MAX_LENGTH),
  emptyConfigurationRow: jsonObjectText(JSON_FIELD_MAX_LENGTH),
  createConfigurationRowSchema: jsonObjectText(),
  configurationDescription: z
    .string()
    .max(TEXT_FIELD_MAX_LENGTH, `is too long (maximum is ${TEXT_FIELD_MAX_LENGTH} characters)`),
  actions: boundedStringArray(SMALL_JSON_FIELD_MAX_LENGTH),
  fees: z.boolean(),
  limits: z.string(),
  logger: z.string(),
  loggerConfiguration: jsonObjectText(SMALL_JSON_FIELD_MAX_LENGTH),
  stagingStorageInput: z.string(),
  stagingStorageOutput: z.string(),
  dataTypeSupport: z.string(),
  allowedProcessorPosition: z.string(),
};

const adminShape = {
  isPublic: z.boolean(),
  isDeprecated: z.boolean(),
  forwardToken: z.boolean(),
  forwardTokenDetails: z.boolean(),
  requiredMemory: z
    .string()
    .refine(
      (value) => value === '' || isMemoryLimit(value),
      'accepts a positive integer, followed by a suffix of b, k, m or g.',
    ),
  processTimeout: z
    .string()
    .refine((value) => value === '' || Number.isFinite(Number(value)), 'is not a number'),
  permissions: jsonArrayText(),
  features: z.array(z.string()),
  complexity: z.string(),
  categories: z.array(z.string()),
};

/**
 * ⚠️ Cross-field rule: a `code-pattern` needs `configurationSchema.supported_components`.
 *
 * - Must be a non-empty array; `{}`, a string and `[]` all fail.
 * - Reported on `configurationSchema`, the editor the user has to change.
 */
const applyCodePatternRule = (
  values: { type: string; configurationSchema: string },
  ctx: z.RefinementCtx,
) => {
  if (values.type !== 'code-pattern') return;

  const result = parseJsonText(values.configurationSchema);
  // Unparseable text already shows its own error.
  if (result.kind === 'invalid') return;

  const supported =
    result.kind === 'parsed' && isJsonObject(result.value)
      ? (result.value as Record<string, unknown>).supported_components
      : undefined;

  if (!Array.isArray(supported) || supported.length < 1) {
    ctx.addIssue({
      code: 'custom',
      path: ['configurationSchema'],
      message: 'Please specify compatible component IDs in "supported_components" parameter.',
    });
  }
};

export const vendorEditAppSchema = z.object(baseShape).superRefine(applyCodePatternRule);

export const adminEditAppSchema = z
  .object({ ...baseShape, ...adminShape })
  .superRefine(applyCodePatternRule);

/**
 * The `.omit()` mask for a data app, from `DATA_APP_HIDDEN_FIELDS`.
 *
 * - Omitted keys are stripped, so a stale hidden value cannot block Save.
 */
const dataAppMask: Partial<Record<(typeof DATA_APP_HIDDEN_FIELDS)[number], true>> =
  Object.fromEntries(DATA_APP_HIDDEN_FIELDS.map((field) => [field, true]));

// No `applyCodePatternRule`: a data app has no `configurationSchema` and is never a code pattern.
const vendorDataAppSchema = z.object(baseShape).omit(dataAppMask);

const adminDataAppSchema = z.object({ ...baseShape, ...adminShape }).omit(dataAppMask);

/** The schema for one form instance — the audience decides the fields, the type decides which apply. */
export const editAppSchemaFor = (
  audience: Audience,
  type: EditAppFormValues['type'],
): z.ZodType => {
  if (!hasConfigurationFields(type)) {
    return audience === 'admin' ? adminDataAppSchema : vendorDataAppSchema;
  }

  return audience === 'admin' ? adminEditAppSchema : vendorEditAppSchema;
};
